export type MiniGameCareerRow = { played: number; net_coins: number; net_rubies: number };

export type QuickfireMiniGameCareerRow = MiniGameCareerRow & { correct_answers: number };

import { parseXpSummary, type XpSummary } from "../../utils/xp";

export type CareerSummaryPayload = {
  xp?: XpSummary | null;
  trader: {
    trades: number;
    won: number;
    profit: number;
    total_winnings: number;
    current_win_streak: number;
    record_win_streak: number;
    biggest_win: number;
  };
  squares: { played: number; won: number; net_winnings: number };
  bullion: { played: number; won: number; net_winnings: number };
  sixes: { played: number; net_winnings: number; biggest_win: number };
  leagues: { played: number; won: number; net_winnings: number };
  mini_games: {
    "wheel-spin": MiniGameCareerRow;
    "quickfire-trivia": QuickfireMiniGameCareerRow;
  };
  totals: { games_played: number; net_coins_won: number };
};

function emptyMiniGamesRow(): MiniGameCareerRow {
  return { played: 0, net_coins: 0, net_rubies: 0 };
}

function emptyQuickfireMiniGamesRow(): QuickfireMiniGameCareerRow {
  return { played: 0, correct_answers: 0, net_coins: 0, net_rubies: 0 };
}

export function parseCareerSummaryPayload(data: unknown): CareerSummaryPayload {
  const d = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const pickMini = (key: string): MiniGameCareerRow | QuickfireMiniGameCareerRow => {
    const raw =
      d.mini_games && typeof d.mini_games === "object" ? (d.mini_games as Record<string, unknown>)[key] : null;
    const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const base = {
      played: Number(o.played) || 0,
      net_coins: Number(o.net_coins) || 0,
      net_rubies: Number(o.net_rubies) || 0,
    };
    if (key === "quickfire-trivia") {
      return { ...base, correct_answers: Number(o.correct_answers) || 0 };
    }
    return base;
  };
  const totalsRaw = d.totals && typeof d.totals === "object" ? (d.totals as Record<string, unknown>) : {};
  const tr = d.trader && typeof d.trader === "object" ? (d.trader as Record<string, unknown>) : {};
  const sq = d.squares && typeof d.squares === "object" ? (d.squares as Record<string, unknown>) : {};
  const fo = d.bullion && typeof d.bullion === "object" ? (d.bullion as Record<string, unknown>) : {};
  const sx = d.sixes && typeof d.sixes === "object" ? (d.sixes as Record<string, unknown>) : {};
  const lg = d.leagues && typeof d.leagues === "object" ? (d.leagues as Record<string, unknown>) : {};
  return {
    xp: parseXpSummary(d.xp),
    trader: {
      trades: Number(tr.trades) || 0,
      won: Number(tr.won) || 0,
      profit: Number(tr.profit) || 0,
      total_winnings: Number(tr.total_winnings) || 0,
      current_win_streak: Number(tr.current_win_streak) || 0,
      record_win_streak: Number(tr.record_win_streak) || 0,
      biggest_win: Number(tr.biggest_win) || 0,
    },
    squares: {
      played: Number(sq.played) || 0,
      won: Number(sq.won) || 0,
      net_winnings: Number(sq.net_winnings) || 0,
    },
    bullion: {
      played: Number(fo.played) || 0,
      won: Number(fo.won) || 0,
      net_winnings: (() => {
        const n = Number(fo.net_winnings);
        return Number.isFinite(n) ? n : 0;
      })(),
    },
    sixes: {
      played: Number(sx.played) || 0,
      net_winnings: (() => {
        const n = Number(sx.net_winnings);
        return Number.isFinite(n) ? n : 0;
      })(),
      biggest_win: (() => {
        const n = Number(sx.biggest_win);
        return Number.isFinite(n) ? n : 0;
      })(),
    },
    leagues: {
      played: Number(lg.played) || 0,
      won: Number(lg.won) || 0,
      net_winnings: (() => {
        const n = Number(lg.net_winnings);
        return Number.isFinite(n) ? n : 0;
      })(),
    },
    mini_games: {
      "wheel-spin": pickMini("wheel-spin") as MiniGameCareerRow,
      "quickfire-trivia": pickMini("quickfire-trivia") as QuickfireMiniGameCareerRow,
    },
    totals: {
      games_played: Number(totalsRaw.games_played) || 0,
      net_coins_won: Number(totalsRaw.net_coins_won) || 0,
    },
  };
}

export function fallbackCareerSummaryZeros(): CareerSummaryPayload {
  return {
    xp: null,
    trader: {
      trades: 0,
      won: 0,
      profit: 0,
      total_winnings: 0,
      current_win_streak: 0,
      record_win_streak: 0,
      biggest_win: 0,
    },
    squares: { played: 0, won: 0, net_winnings: 0 },
    bullion: { played: 0, won: 0, net_winnings: 0 },
    sixes: { played: 0, net_winnings: 0, biggest_win: 0 },
    leagues: { played: 0, won: 0, net_winnings: 0 },
    mini_games: {
      "wheel-spin": emptyMiniGamesRow(),
      "quickfire-trivia": emptyQuickfireMiniGamesRow(),
    },
    totals: { games_played: 0, net_coins_won: 0 },
  };
}

/**
 * Public profile `career` from API. Always normalizes through {@link parseCareerSummaryPayload}
 * so Quickfire (and wheel) mini-game stats use the same shape as `/account/career-summary`
 * (Quickfire counts/coins come from `quickfire_plays` on the server).
 */
export function coercePublicCareer(career: unknown): CareerSummaryPayload | null {
  if (!career || typeof career !== "object") return null;
  return parseCareerSummaryPayload(career);
}
